import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  getAnalytics,
  getAnalyticsEvents,
  type AnalyticsResponse,
  type AnalyticsEventsResponse,
} from '../../shared/api/analytics';
import { getPublicCategories } from '../../shared/api/categories';
import { getPublicTags } from '../../shared/api/tags';
import type { Tag } from '../../shared/api/tags';
import type { Category } from '../../shared/types/header';
import { useLanguage } from '../../shared/hooks/useLanguage';
import { useMetaTags } from '../../shared/hooks/useMetaTags';
import { getSiteUrl } from '../../shared/utils/metaTags';
import {
  getDisplayTitleForPath,
  resolveCategoryDisplayTitle,
  resolveTagDisplayTitle,
  isAnalyticsPage,
} from '../../shared/utils/pageDisplay';
import { pagePathForDisplayLang } from '../../shared/utils/urlUtils';
import { LoadingSpinner } from '../../shared/components/LoadingSpinner';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from 'recharts';
import './AppsAnalytics.css';

const DAYS_OPTIONS = [7, 14, 30] as const;
const PAGE_SIZE = 10;

const PIE_COLORS = ['#4b6cb7', '#6b8e23', '#c97064', '#7b68ee', '#20b2aa', '#daa520', '#cd5c5c', '#4682b4', '#2e8b57', '#9370db'];

/** 円グラフに表示する最大項目数。それ以外は「その他」にまとめる */
const MAX_PIE_ITEMS = 5;

type SortKey = 'timestamp' | 'page_path' | 'country' | 'device_type' | 'browser';
type SortOrder = 'asc' | 'desc';

/** ユーザーのローカルタイムゾーンで日時を表示する */
function formatTimestamp(iso: string, lang: string): string {
  try {
    const d = new Date(iso);
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return lang === 'ja'
      ? d.toLocaleString('ja-JP', {
          timeZone: tz,
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
        })
      : d.toLocaleString('en-US', {
          timeZone: tz,
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
        });
  } catch {
    return iso;
  }
}

function referrerDisplay(referrer: string | undefined): string {
  if (!referrer || !referrer.trim()) return '—';
  try {
    const url = new URL(referrer);
    return url.hostname;
  } catch {
    return referrer.length > 40 ? referrer.slice(0, 40) + '…' : referrer;
  }
}

/**
 * 円グラフ用に次元データを集約する。上位 MAX_PIE_ITEMS 件を表示し、残りは「その他」にまとめる。
 */
/** 円グラフに表示しないラベル（空欄・未設定を示す値） */
const BLANK_PIE_NAMES = ['', '—'];

function buildPieData(
  items: Array<{ name: string; count: number }>,
  lang: string
): Array<{ name: string; value: number }> {
  const sorted = [...items]
    .filter((d) => d.count > 0 && !BLANK_PIE_NAMES.includes((d.name || '').trim()))
    .sort((a, b) => b.count - a.count);
  if (sorted.length === 0) return [];
  const top = sorted.slice(0, MAX_PIE_ITEMS);
  const rest = sorted.slice(MAX_PIE_ITEMS);
  const restSum = rest.reduce((acc, d) => acc + d.count, 0);
  const result = top.map((d) => ({ name: d.name, value: d.count }));
  if (restSum > 0) {
    result.push({ name: lang === 'ja' ? 'その他' : 'Other', value: restSum });
  }
  return result;
}

/**
 * 詳細アクセス履歴ページ
 * サマリー・時系列グラフ・イベント一覧（ソート・ページネーション）
 */
function AppsAnalytics() {
  const lang = useLanguage();
  const [days, setDays] = useState<7 | 14 | 30>(7);
  const [summary, setSummary] = useState<AnalyticsResponse | null>(null);
  const [eventsData, setEventsData] = useState<AnalyticsEventsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [offset, setOffset] = useState(0);
  const [sortKey, setSortKey] = useState<SortKey>('timestamp');
  const [sortOrder, setSortOrder] = useState<SortOrder>('desc');
  const [expandedRowId, setExpandedRowId] = useState<string | null>(null);
  const [isMobile, setIsMobile] = useState(true);
  const [isPieChartsOpen, setPieChartsOpen] = useState(false);
  const [isConsentNoteOpen, setConsentNoteOpen] = useState(false);
  const [categories, setCategories] = useState<Category[]>([]);
  const [tags, setTags] = useState<Tag[]>([]);

  const siteUrl = getSiteUrl();
  const analyticsUrl = `${siteUrl}/${lang}/apps/analytics`;
  const titleJa = 'アクセス履歴';
  const titleEn = 'Access History';
  const title = lang === 'ja' ? titleJa : titleEn;
  const description =
    lang === 'ja'
      ? '期間別のアクセス数・国・デバイスなどの詳細を確認できます。'
      : 'View detailed access data by period: counts, countries, devices, and more.';

  useMetaTags({
    title: `${title} | Version0.1`,
    description,
    ogp: {
      title: `${title} | Version0.1`,
      description,
      url: analyticsUrl,
      type: 'website',
      siteName: 'Version0.1',
      image: '/og-image.png',
    },
    twitterCard: {
      card: 'summary' as const,
      title: `${title} | Version0.1`,
      description,
      image: '/og-image.png',
    },
    canonicalUrl: analyticsUrl,
  });

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    Promise.all([
      getAnalytics(days, lang),
      getAnalyticsEvents(days, PAGE_SIZE, 0, lang),
    ])
      .then(([sum, ev]) => {
        if (!cancelled) {
          setSummary(sum);
          setEventsData(ev);
          setOffset(0);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Failed to load');
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [days, lang]);

  useEffect(() => {
    const mq = window.matchMedia('(max-width: 767px)');
    const update = () => {
      const mobile = mq.matches;
      setIsMobile(mobile);
      if (mobile) {
        setPieChartsOpen(false);
      } else {
        setPieChartsOpen(true);
      }
    };
    update();
    mq.addEventListener('change', update);
    return () => mq.removeEventListener('change', update);
  }, []);

  useEffect(() => {
    getPublicCategories()
      .then((res) => setCategories(res.categories ?? []))
      .catch(() => setCategories([]));
  }, []);

  useEffect(() => {
    getPublicTags()
      .then((res) => setTags(res.tags ?? []))
      .catch(() => setTags([]));
  }, []);

  const fetchMoreEvents = (newOffset: number) => {
    setLoading(true);
    getAnalyticsEvents(days, PAGE_SIZE, newOffset, lang)
      .then((data) => {
        setEventsData(data);
        setOffset(newOffset);
      })
      .catch((err) => setError(err instanceof Error ? err.message : 'Failed to load'))
      .finally(() => setLoading(false));
  };

  const handleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      setSortOrder('desc');
    }
  };

  const events = eventsData?.events ?? [];
  const total = eventsData?.total ?? 0;
  const rawDailyCounts = eventsData?.daily_counts ?? [];
  const dimensionCounts = eventsData?.dimension_counts ?? {};
  const period = eventsData?.period ?? { start: '', end: '' };

  const chartData = useMemo(() => {
    const byDay = new Map<string, number>(rawDailyCounts.map((d) => [d.date, d.count]));
    let startUtc: string;
    let endUtc: string;
    if (period.start && period.end) {
      startUtc = period.start.slice(0, 10);
      const endDate = new Date(period.end);
      endUtc =
        endDate.getUTCFullYear() +
        '-' +
        String(endDate.getUTCMonth() + 1).padStart(2, '0') +
        '-' +
        String(endDate.getUTCDate()).padStart(2, '0');
    } else if (rawDailyCounts.length > 0) {
      const dates = rawDailyCounts.map((d) => d.date).sort();
      startUtc = dates[0];
      endUtc = dates[dates.length - 1];
    } else {
      return [];
    }
    const days: Array<{ date: string; count: number }> = [];
    const d = new Date(startUtc + 'T00:00:00.000Z');
    const end = new Date(endUtc + 'T00:00:00.000Z');
    while (d <= end) {
      const key = d.toISOString().slice(0, 10);
      days.push({ date: key, count: byDay.get(key) ?? 0 });
      d.setUTCDate(d.getUTCDate() + 1);
    }
    return days;
  }, [rawDailyCounts, period.start, period.end]);

  const uniquePageCount = useMemo(() => {
    if (typeof eventsData?.unique_page_count === 'number') return eventsData.unique_page_count;
    return new Set(
      events.map((e) => (e.page_path ?? '').toString().trim()).filter((p) => p !== ''),
    ).size;
  }, [eventsData?.unique_page_count, events]);
  const totalViews = typeof eventsData?.total === 'number' ? eventsData.total : (summary?.total_views ?? 0);
  const uniqueVisitors = typeof eventsData?.unique_visitors === 'number' ? eventsData.unique_visitors : (summary?.unique_visitors ?? 0);
  const topCountryRaw = eventsData?.top_country ?? summary?.top_countries?.[0]?.country ?? null;
  const topCountry =
    topCountryRaw != null && (topCountryRaw as string).trim() !== '' && (topCountryRaw as string).trim() !== '—'
      ? topCountryRaw
      : null;

  const filteredAndSortedEvents = useMemo(() => {
    const list = [...events];
    list.sort((a, b) => {
      const aVal = a[sortKey] ?? '';
      const bVal = b[sortKey] ?? '';
      const cmp = String(aVal).localeCompare(String(bVal), undefined, { numeric: true });
      return sortOrder === 'asc' ? cmp : -cmp;
    });
    return list;
  }, [events, sortKey, sortOrder]);

  if (loading && !summary && !eventsData) {
    return (
      <main className="apps-analytics-page">
        <div className="apps-analytics-page__container">
          <div className="apps-analytics-page__loading">
            <LoadingSpinner text={lang === 'ja' ? '読み込み中...' : 'Loading...'} />
          </div>
        </div>
      </main>
    );
  }

  if (error && !summary && !eventsData) {
    return (
      <main className="apps-analytics-page">
        <div className="apps-analytics-page__container">
          <p className="apps-analytics-page__error">{error}</p>
        </div>
      </main>
    );
  }

  return (
    <main className="apps-analytics-page">
      <div className="apps-analytics-page__container">
        <div className="apps-analytics-page__title-row">
          <h1 className="apps-analytics-page__title">{title}</h1>
          <button
            type="button"
            className="apps-analytics-page__info-btn"
            onClick={() => setConsentNoteOpen(!isConsentNoteOpen)}
            aria-expanded={isConsentNoteOpen}
            aria-label={lang === 'ja' ? 'データの収集についての説明を表示' : 'Show data collection explanation'}
            title={lang === 'ja' ? 'データの収集について' : 'About data collection'}
          >
            <span className="apps-analytics-page__info-icon" aria-hidden>i</span>
          </button>
        </div>

        {/* 許諾についての説明（インフォアイコンで表示切替） */}
        {isConsentNoteOpen && (
        <div className="apps-analytics-page__consent-note" role="region" aria-label={lang === 'ja' ? 'アクセス解析の許諾について' : 'About analytics consent'}>
          <h2 className="apps-analytics-page__consent-note-title">
            {lang === 'ja' ? 'データの収集について' : 'About data collection'}
          </h2>
          <ul className="apps-analytics-page__consent-note-list">
            <li>
              {lang === 'ja' ? '常時送信（許諾不要）' : 'Always sent (no consent required)'}
              {': '}
              {lang === 'ja' ? 'ページパス、参照元。国・地域・都市・OS・ブラウザはサーバ側で記録。' : 'Page path, referrer. Country, region, city, OS, browser recorded server-side.'}
            </li>
            <li>
              {lang === 'ja' ? '許諾時のみ送信' : 'Sent only with consent'}
              {': '}
              {lang === 'ja' ? 'Chrome の高エントロピー Client Hints（機種名等）。' : 'Chrome high-entropy Client Hints (e.g. device model).'}
              {' '}
              <a
                href="https://developer.mozilla.org/en-US/docs/Web/HTTP/Reference/Headers/Permissions-Policy/ch-ua-high-entropy-values"
                target="_blank"
                rel="noopener noreferrer"
                className="apps-analytics-page__consent-note-link"
              >
                {lang === 'ja' ? '根拠（MDN）' : 'Reference (MDN)'}
              </a>
            </li>
            <li>
              {lang === 'ja' ? '機種名はモバイル・タブレットでのみ表示。デスクトップは空。設定で変更可。' : 'Device model: mobile/tablet only; empty on desktop. Change in Settings.'}
            </li>
          </ul>
        </div>
        )}

        {/* 期間 */}
        <div className="apps-analytics-page__period">
          <span className="apps-analytics-page__period-label">
            {lang === 'ja' ? '期間' : 'Period'}:
          </span>
          {DAYS_OPTIONS.map((d) => (
            <button
              key={d}
              type="button"
              className={`apps-analytics-page__period-btn ${days === d ? 'apps-analytics-page__period-btn--active' : ''}`}
              onClick={() => setDays(d)}
            >
              {d} {lang === 'ja' ? '日間' : 'days'}
            </button>
          ))}
        </div>

        {/* KPI */}
        <div className="apps-analytics-page__kpi">
          <div className="apps-analytics-page__kpi-card">
            <span className="apps-analytics-page__kpi-value">{totalViews.toLocaleString()}</span>
            <span className="apps-analytics-page__kpi-label">
              {lang === 'ja' ? '総アクセス数' : 'Total views'}
            </span>
          </div>
          <div className="apps-analytics-page__kpi-card">
            <span className="apps-analytics-page__kpi-value">{uniqueVisitors.toLocaleString()}</span>
            <span className="apps-analytics-page__kpi-label">
              {lang === 'ja' ? 'ユニークユーザー' : 'Unique users'}
            </span>
          </div>
          <div className="apps-analytics-page__kpi-card">
            <span className="apps-analytics-page__kpi-value">{uniquePageCount.toLocaleString()}</span>
            <span className="apps-analytics-page__kpi-label">
              {lang === 'ja' ? 'ユニークページ数' : 'Unique pages'}
            </span>
          </div>
          <div className="apps-analytics-page__kpi-card">
            <span className="apps-analytics-page__kpi-value">{topCountry ?? '—'}</span>
            <span className="apps-analytics-page__kpi-label">
              {lang === 'ja' ? '最多アクセス国' : 'Top country'}
            </span>
          </div>
        </div>

        {/* 時系列グラフ */}
        <div className="apps-analytics-page__chart">
          <h2 className="apps-analytics-page__chart-title">
            {lang === 'ja' ? '日別アクセス数' : 'Daily views'}
          </h2>
          {chartData.length > 0 ? (
            <ResponsiveContainer width="100%" height={240}>
              <AreaChart data={chartData} margin={{ top: 8, right: 8, left: 8, bottom: 8 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                <XAxis
                  dataKey="date"
                  tick={{ fontSize: 12 }}
                  tickFormatter={(dateStr: string) =>
                    new Date(dateStr + 'T12:00:00.000Z').toLocaleDateString(
                      lang === 'ja' ? 'ja-JP' : 'en-US',
                      { timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone, month: 'numeric', day: 'numeric' },
                    )
                  }
                />
                <YAxis tick={{ fontSize: 12 }} allowDecimals={false} />
                <Tooltip
                  labelFormatter={(label: string) =>
                    new Date(label + 'T12:00:00.000Z').toLocaleDateString(
                      lang === 'ja' ? 'ja-JP' : 'en-US',
                      {
                        timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
                        year: 'numeric',
                        month: '2-digit',
                        day: '2-digit',
                      },
                    )
                  }
                  formatter={(value: number) => [value, lang === 'ja' ? 'アクセス数' : 'Views']}
                />
                <Area
                  type="monotone"
                  dataKey="count"
                  stroke="#4b6cb7"
                  fill="#4b6cb7"
                  fillOpacity={0.3}
                />
              </AreaChart>
            </ResponsiveContainer>
          ) : (
            <p className="apps-analytics-page__chart-empty">
              {lang === 'ja' ? 'この期間のデータはありません' : 'No data for this period'}
            </p>
          )}
        </div>

        {/* 国・OS・デバイス・ブラウザ・機種の円グラフ（スマホではデフォルト折りたたみ） */}
        <section className="apps-analytics-page__pies-section" aria-labelledby="apps-analytics-pies-heading" data-testid="apps-analytics-pies-section">
          {isMobile && (
            <button
              type="button"
              id="apps-analytics-pies-heading"
              className="apps-analytics-page__pies-toggle"
              onClick={() => setPieChartsOpen(!isPieChartsOpen)}
              aria-expanded={isPieChartsOpen}
            >
              <span className="apps-analytics-page__pies-toggle-text">
                {lang === 'ja' ? '円グラフ' : 'Pie charts'}
              </span>
              <span className="apps-analytics-page__pies-toggle-icon" aria-hidden>
                {isPieChartsOpen ? '▼' : '▶'}
              </span>
            </button>
          )}
          {(!isMobile || isPieChartsOpen) && (
        <div className="apps-analytics-page__pies">
          {(
            [
              { key: 'country' as const, titleJa: '国', titleEn: 'Country' },
              { key: 'os' as const, titleJa: 'OS', titleEn: 'OS' },
              { key: 'device_type' as const, titleJa: 'デバイス', titleEn: 'Device' },
              { key: 'browser' as const, titleJa: 'ブラウザ', titleEn: 'Browser' },
              { key: 'device_model' as const, titleJa: '機種', titleEn: 'Model' },
              { key: 'referrer' as const, titleJa: '参照元', titleEn: 'Referrer' },
            ] as const
          ).map(({ key, titleJa, titleEn }) => {
            const data = dimensionCounts[key] ?? [];
            const pieData = buildPieData(data, lang);
            const hasData = pieData.length > 0;
            const total = pieData.reduce((sum, d) => sum + d.value, 0);
            const toPct = (value: number) =>
              total ? ((value / total) * 100).toFixed(1) + '%' : '0%';
            return (
              <div key={key} className="apps-analytics-page__pie-wrap">
                <h3
                  className="apps-analytics-page__pie-title"
                  title={key === 'device_model' ? (lang === 'ja' ? 'モバイル・タブレットで表示。デスクトップでは空になります。' : 'Shown for mobile and tablet; empty on desktop.') : undefined}
                >
                  {lang === 'ja' ? titleJa : titleEn}
                </h3>
                {hasData ? (
                  <div className="apps-analytics-page__pie-body">
                    <div className="apps-analytics-page__pie-chart">
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart margin={{ top: 4, right: 4, bottom: 4, left: 4 }}>
                          <Pie
                            data={pieData}
                            dataKey="value"
                            nameKey="name"
                            cx="50%"
                            cy="50%"
                            outerRadius="80%"
                          >
                            {pieData.map((_, index) => (
                              <Cell key={`cell-${index}`} fill={PIE_COLORS[index % PIE_COLORS.length]} />
                            ))}
                          </Pie>
                          <Tooltip
                            content={({ active, payload }) => {
                              if (!active || !payload?.length) return null;
                              const name = payload[0].name ?? '';
                              const value = Number(payload[0].value) || 0;
                              const pct = toPct(value);
                              return (
                                <div className="apps-analytics-page__pie-tooltip">
                                  {name}: {pct}
                                </div>
                              );
                            }}
                          />
                        </PieChart>
                      </ResponsiveContainer>
                    </div>
                    <ul className="apps-analytics-page__pie-legend" aria-hidden>
                      {pieData.map((d, index) => (
                        <li key={`${d.name}-${index}`} className="apps-analytics-page__pie-legend-item">
                          <span
                            className="apps-analytics-page__pie-legend-dot"
                            style={{ backgroundColor: PIE_COLORS[index % PIE_COLORS.length] }}
                          />
                          <span className="apps-analytics-page__pie-legend-label" title={d.name}>
                            {d.name}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                ) : (
                  <p className="apps-analytics-page__pie-empty">{lang === 'ja' ? 'データなし' : 'No data'}</p>
                )}
              </div>
            );
          })}
        </div>
          )}
        </section>

        {/* アクセス一覧 */}
        <div className="apps-analytics-page__section">
          <div className="apps-analytics-page__section-header">
            <h2 className="apps-analytics-page__section-title">
              {lang === 'ja' ? 'アクセス一覧' : 'Access list'}
            </h2>
          </div>

          <div className="apps-analytics-page__table-wrap">
            <table className="apps-analytics-page__table">
              <thead>
                <tr>
                  <th className="apps-analytics-page__th apps-analytics-page__th--expand-mobile" aria-hidden="true" />
                  <th
                    className="apps-analytics-page__th apps-analytics-page__th--date apps-analytics-page__th--sortable"
                    onClick={() => handleSort('timestamp')}
                  >
                    {lang === 'ja' ? '日時' : 'Date'}
                    {sortKey === 'timestamp' && (sortOrder === 'asc' ? ' ↑' : ' ↓')}
                  </th>
                  <th
                    className="apps-analytics-page__th apps-analytics-page__th--sortable"
                    onClick={() => handleSort('page_path')}
                  >
                    {lang === 'ja' ? 'ページ' : 'Page'}
                    {sortKey === 'page_path' && (sortOrder === 'asc' ? ' ↑' : ' ↓')}
                  </th>
                  <th
                    className="apps-analytics-page__th apps-analytics-page__th--sortable apps-analytics-page__th--mobile-collapse"
                    onClick={() => handleSort('country')}
                  >
                    {lang === 'ja' ? '国' : 'Country'}
                    {sortKey === 'country' && (sortOrder === 'asc' ? ' ↑' : ' ↓')}
                  </th>
                  <th className="apps-analytics-page__th apps-analytics-page__th--mobile-collapse">{lang === 'ja' ? '地域' : 'Region'}</th>
                  <th className="apps-analytics-page__th apps-analytics-page__th--mobile-collapse">{lang === 'ja' ? '参照元' : 'Referrer'}</th>
                  <th className="apps-analytics-page__th apps-analytics-page__th--mobile-collapse">{lang === 'ja' ? 'OS' : 'OS'}</th>
                  <th
                    className="apps-analytics-page__th apps-analytics-page__th--sortable apps-analytics-page__th--mobile-collapse"
                    onClick={() => handleSort('device_type')}
                  >
                    {lang === 'ja' ? 'デバイス' : 'Device'}
                    {sortKey === 'device_type' && (sortOrder === 'asc' ? ' ↑' : ' ↓')}
                  </th>
                  <th
                    className="apps-analytics-page__th apps-analytics-page__th--sortable apps-analytics-page__th--mobile-collapse"
                    onClick={() => handleSort('browser')}
                  >
                    {lang === 'ja' ? 'ブラウザ' : 'Browser'}
                    {sortKey === 'browser' && (sortOrder === 'asc' ? ' ↑' : ' ↓')}
                  </th>
                  <th
                  className="apps-analytics-page__th apps-analytics-page__th--mobile-collapse"
                  title={lang === 'ja' ? 'モバイル・タブレットで表示。デスクトップでは空になります。' : 'Shown for mobile and tablet; empty on desktop.'}
                >
                  {lang === 'ja' ? '機種' : 'Model'}
                </th>
                </tr>
              </thead>
              <tbody>
                {filteredAndSortedEvents.map((ev) => {
                  const rowId = ev.event_id ?? ev.timestamp + ev.page_path;
                  const isExpanded = expandedRowId === rowId;
                  return (
                    <React.Fragment key={rowId}>
                      <tr className="apps-analytics-page__row">
                        <td className="apps-analytics-page__td apps-analytics-page__td--expand-mobile">
                          <button
                            type="button"
                            className="apps-analytics-page__expand-btn"
                            data-testid="apps-analytics-expand-row"
                            onClick={() => setExpandedRowId(isExpanded ? null : rowId)}
                            aria-expanded={isExpanded}
                            aria-label={isExpanded ? (lang === 'ja' ? '詳細を閉じる' : 'Collapse') : (lang === 'ja' ? '詳細を開く' : 'Expand')}
                          >
                            {isExpanded ? '−' : '+'}
                          </button>
                        </td>
                        <td className="apps-analytics-page__td apps-analytics-page__td--date">
                          {formatTimestamp(ev.timestamp, lang)}
                        </td>
                        <td className="apps-analytics-page__td apps-analytics-page__td--path">
                          {(() => {
                            const fromPath = getDisplayTitleForPath(ev.page_path, lang);
                            let resolved = resolveCategoryDisplayTitle(fromPath, lang, categories);
                            resolved = resolveTagDisplayTitle(resolved, lang, tags);
                            const fromApi = ev.title?.trim();
                            const useApiTitle = fromApi && !fromApi.startsWith('/');
                            const displayText =
                              (resolved ?? fromPath ?? (useApiTitle ? fromApi : null) ?? ev.page_path) || '—';
                            if (!ev.page_path) return displayText;
                            const linkPath = pagePathForDisplayLang(ev.page_path, lang);
                            if (isAnalyticsPage(ev.page_path, lang)) {
                              return <span className="apps-analytics-page__page-text">{displayText}</span>;
                            }
                            return (
                              <Link to={linkPath} className="apps-analytics-page__page-link">
                                {displayText}
                              </Link>
                            );
                          })()}
                        </td>
                        <td className="apps-analytics-page__td apps-analytics-page__td--mobile-collapse">{ev.country || '—'}</td>
                        <td className="apps-analytics-page__td apps-analytics-page__td--hide-mobile apps-analytics-page__td--mobile-collapse">
                          {ev.country_region_name || '—'}
                        </td>
                        <td className="apps-analytics-page__td apps-analytics-page__td--referrer apps-analytics-page__td--mobile-collapse">
                          {referrerDisplay(ev.referrer)}
                        </td>
                        <td className="apps-analytics-page__td apps-analytics-page__td--mobile-collapse">{ev.os || '—'}</td>
                        <td className="apps-analytics-page__td apps-analytics-page__td--mobile-collapse">{ev.device_type || '—'}</td>
                        <td className="apps-analytics-page__td apps-analytics-page__td--mobile-collapse">{ev.browser || '—'}</td>
                        <td className="apps-analytics-page__td apps-analytics-page__td--hide-mobile apps-analytics-page__td--mobile-collapse">
                          {ev.device_model || '—'}
                        </td>
                      </tr>
                      {isExpanded && (
                        <tr className="apps-analytics-page__row apps-analytics-page__row--detail">
                          <td colSpan={10} className="apps-analytics-page__detail-cell">
                            <dl className="apps-analytics-page__detail-dl">
                              <div>
                                <dt>{lang === 'ja' ? '国' : 'Country'}</dt>
                                <dd>{ev.country || '—'}</dd>
                              </div>
                              <div>
                                <dt>{lang === 'ja' ? '地域' : 'Region'}</dt>
                                <dd>{ev.country_region_name || '—'}</dd>
                              </div>
                              <div>
                                <dt>{lang === 'ja' ? '参照元' : 'Referrer'}</dt>
                                <dd>{ev.referrer ? referrerDisplay(ev.referrer) : '—'}</dd>
                              </div>
                              <div>
                                <dt>{lang === 'ja' ? 'OS' : 'OS'}</dt>
                                <dd>{ev.os || '—'}</dd>
                              </div>
                              <div>
                                <dt>{lang === 'ja' ? 'デバイス' : 'Device'}</dt>
                                <dd>{ev.device_type || '—'}</dd>
                              </div>
                              <div>
                                <dt>{lang === 'ja' ? 'ブラウザ' : 'Browser'}</dt>
                                <dd>{ev.browser || '—'}</dd>
                              </div>
                              <div>
                                <dt>{lang === 'ja' ? '機種' : 'Model'}</dt>
                                <dd>{ev.device_model || '—'}</dd>
                              </div>
                            </dl>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>

          {filteredAndSortedEvents.length === 0 && (
            <p className="apps-analytics-page__empty">
              {lang === 'ja' ? 'データがありません' : 'No data'}
            </p>
          )}

          {/* ページネーション */}
          {total > PAGE_SIZE && (
            <div className="apps-analytics-page__pagination">
              <button
                type="button"
                className="apps-analytics-page__page-btn"
                disabled={offset === 0 || loading}
                onClick={() => fetchMoreEvents(Math.max(0, offset - PAGE_SIZE))}
              >
                {lang === 'ja' ? '前へ' : 'Previous'}
              </button>
              <button
                type="button"
                className="apps-analytics-page__page-btn"
                disabled={offset + PAGE_SIZE >= total || loading}
                onClick={() => fetchMoreEvents(offset + PAGE_SIZE)}
              >
                {lang === 'ja' ? '次へ' : 'Next'}
              </button>
            </div>
          )}
        </div>
      </div>
    </main>
  );
}

export default AppsAnalytics;
